import { parseHashParams } from '@/router/util';
import { AuthService } from "@/services/auth.service";
import Vue from 'vue';
import { Capacitor } from '@capacitor/core';
import KeychainPlugin from '@/plugins/keychain';
import { KeychainHandler } from '@/plugins/keychain/plugin';

// FontAwesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';

// mods
import VueIcons from 'vue-icon';
import VueLodash from 'vue-lodash';
import lodash from 'lodash';
import VueTheMask from './directives/mask';
import VueLazyload from 'vue-lazyload';
import VueContentPlaceholders from 'vue-content-placeholders';
import CoreMedia from '@/assets/plugins/coremedia/coremedia.js';
import CommentsBox from '@/components/CommentsBox.vue';
import VueGtag from "vue-gtag";

// pages
import AppLayout from './layout/Index.vue';

import router from './router';
import store from './store';
import './mixins/currentUser.js';
import './mixins/index.js';

import 'cropperjs/dist/cropper.css';
import 'materialize-css/dist/css/materialize.min.css';
import 'material-icons-font/material-icons-font.css';
import '@/assets/plugins/coremedia/coremedia.css';
import '@/assets/plugins/coremedia/coremedia-vjs-theme-vxl.css';
import '@/assets/plugins/downloader/downloader.js';
import '@/services/onlineoffline.service.js'
import { downloadNotification } from '@/services/downloadNotification';
import { initPush } from '@/services/push/pushRegistration';
import { offlineStorage } from '@/services/offline/offlineStorage';
import { imageCache } from '@/services/offline/imageCache';
import { Network } from '@capacitor/network';
import VModal from 'vue-js-modal';
import VueDirectiveTooltip from 'vue-directive-tooltip';
import VueTheme from '@/themes';

import 'vue-directive-tooltip/dist/vueDirectiveTooltip.css'

// Configure FontAwesome
library.add(fas);
Vue.component('font-awesome-icon', FontAwesomeIcon);

Vue.use(VueIcons, 'v-icon');
Vue.use(require("vue-materialize/side-nav"));
Vue.use(VueLodash, { lodash: lodash });
Vue.use(VueTheme);

Vue.use(VueTheMask);
Vue.use(VueContentPlaceholders);
Vue.use(VueLazyload, {
  error: require('@/assets/images/posters/video.png'),
  attempt: 1
});
Vue.use(CoreMedia, store);
Vue.use(VModal);
Vue.use(VueDirectiveTooltip);
Vue.use(KeychainPlugin);
Vue.use(VueGtag, {
  config: {
    id: process.env.VUE_APP_UA,
    params: {
      send_page_view: false
    }
  }
});

Vue.component('CommentsBox', CommentsBox);

import './scss/style.scss'
import './assets/fonts/fonts.css'
import './assets/css/font-awesome.min.css'

const isApp = Capacitor.isNativePlatform()
Vue.prototype.$isApp = isApp
Vue.prototype.$isIos = Capacitor.getPlatform() === 'ios'

// Push remoto + Universal Links (STORY-1642). ANTES dos awaits do boot e do
// `new Vue`: os listeners de toque (`notificationActionPerformed`) e de link
// (`appUrlOpen`/`getLaunchUrl`) existem desde o início, e o destino fica
// pendente até o router e a auth resolverem — quem navega é o pushRouter, não
// este arquivo. Na web só liga o roteador (usado pelo clique no sino).
initPush({ store, router });

// Promise única do Network.getStatus reutilizada entre o commit inicial e o
// boot resume (B5). Antes eram 2 chamadas bridge separadas — agora 1.
let bootNetworkStatus = null;

if (isApp) {
  // Marca o root pra CSS que só deve valer no app nativo (não no site mobile,
  // que também cai nos media queries de largura). Ex.: layout da lista de aulas.
  document.documentElement.classList.add('is-app');

  // downloadNotification não é mais inicializada aqui — o init virou lazy,
  // disparado dentro de downloadNotification.start() na primeira vez que o user
  // de fato toca em "baixar". Evita prompt de permissão no boot quando o user
  // ainda nem entendeu o que o app faz.

  // Conectividade real ANTES de tudo (migrate/hydrate/$mount). O default de
  // `isConnected` é `true`; o middleware de rota faz request de rede no branch
  // online e, offline (modo avião, SEM timeout), essa request trava pra sempre →
  // Promise.all(...).then(next) nunca chama next() → navegação não completa →
  // tela branca. Por isso AWAIT o status e commito antes de qualquer navegação,
  // em vez de deixar o commit pra um .then que pode resolver depois do middleware.
  // navigator.onLine é não-confiável no WebView Android — usar Capacitor Network.
  try {
    const bootStatus = await Network.getStatus();
    store.commit('dom/SET_CONNECTED', bootStatus.connected);
    store.commit('dom/SET_CONNECTION_TYPE', bootStatus.connectionType);
    // bootNetworkStatus é reusado pelo boot resume mais abaixo (sem novo round-trip).
    bootNetworkStatus = Promise.resolve(bootStatus);
  } catch (e) {
    // getStatus falhar é raríssimo, mas não pode travar o boot. Mantém o default
    // (online) — comportamento equivalente ao .then sem catch de antes.
    console.warn('[boot] Network.getStatus falhou:', e?.message || e);
    bootNetworkStatus = Promise.resolve({ connected: true, connectionType: 'unknown' });
  }

  // migrateLegacyDownloads: purga arquivos plaintext do cache offline anterior à
  // cifragem local (no-op no caso comum). Awaited pra sequenciar com o hydrate:
  // se ela roda, faz rmdir('offline') — e o hydrate não pode ler um structure.json
  // prestes a ser apagado.
  // hydrate: carrega o snapshot de metadados offline (estrutura/grids/enrolls/
  // imageMap) do Filesystem pra memória ANTES de qualquer read offline (boot
  // resume, páginas montando) — os reads do offlineStorage são síncronos sobre ele.
  //
  // try/catch defensivo: um await que rejeite AQUI travaria o boot antes do
  // $mount (tela branca). Hoje nenhum dos dois rejeita, mas degradar gracioso
  // (seguir pro mount com snapshot vazio) é mais seguro que arriscar o branco.
  try {
    await store.dispatch('download/migrateLegacyDownloads');
    await offlineStorage.hydrate();
  } catch (e) {
    console.warn('[boot] migrate/hydrate offline falhou:', e?.message || e);
  }

  // GC one-shot do lixo do imageMap/ead-images (resíduo do re-cache de URL local
  // pré-f3f03d4). Fire-and-forget — nada do boot depende disso. Gated por flag pra
  // não readdir a cada boot. Poda o map primeiro (síncrono) pra já remover o
  // rewrite quebrado neste boot, mesmo que a deleção de arquivo falhe.
  if (!localStorage.getItem('ead_image_gc_v1')) {
    offlineStorage.pruneLocalImageMapKeys();
    imageCache.gcLocalImageFiles()
      .catch(() => {})
      .finally(() => localStorage.setItem('ead_image_gc_v1', '1'));
  }

  Network.addListener('networkStatusChange', async ({ connected, connectionType }) => {
    console.log('[Network] connected=' + connected);
    const wasOffline = !store.state.dom.isConnected;
    const prevConnectionType = store.state.dom.connectionType;
    store.commit('dom/SET_CONNECTED', connected);
    store.commit('dom/SET_CONNECTION_TYPE', connectionType);

    // Voltou pro Wi-Fi vindo de outra rede, SEM ter ficado offline. Um download
    // wifi-only pausado por troca pra cellular não passava por `wasOffline`
    // (nunca houve desconexão), então ficava âmbar até o app reiniciar ou o user
    // tocar aula por aula — sem nada indicando que bastava tocar.
    if (connected && !wasOffline && connectionType === 'wifi' && prevConnectionType && prevConnectionType !== 'wifi') {
      store.dispatch('download/resumePausedDownloads', { connectionType });
    }

    if (wasOffline && connected) {
      await store.dispatch('auth/refreshTokensOnReconnect');
      store.dispatch('enroll/flushOfflineEventQueue');
      // Retoma downloads pausados por queda de rede. Tem que rodar DEPOIS do
      // refresh de token (await acima) — senão as requests do plugin nativo
      // vão com token expirado e falham com 401 antes de chegar nos segmentos.
      // connectionType é passado pra honrar a preferência wifi-only do user
      // (resumePausedDownloads pula auto-resume em cellular se downloadOnCellular=false).
      store.dispatch('download/resumePausedDownloads', { connectionType });
      // Renovação do lease passa pela lista FRESCA de enrolls: getUserEnroll
      // dispara lease/syncEntitlements, que revoga cursos bloqueados/ausentes e
      // renova só os liberados. No reconnect não há navegação que rode isso,
      // por isso o disparo explícito (em vez de renovar cegamente).
      store.dispatch('enroll/getUserEnroll')
        // Cura da IA das aulas baixadas que ficaram sem ai.json, encadeada no
        // getUserEnroll em vez de disparada solta: ela faz request autenticada, e
        // este é o ponto que garante token instalado no axios E a lista de
        // enrolls carregada, que o gate de enableIA consulta. Ver o mesmo padrão
        // no boot resume, onde disparar solto devolvia 400.
        .then(() => store.dispatch('download/healMissingAiCache'))
        .then(() => store.dispatch('download/healMissingSubtitles'))
        .catch(() => {});
    }
  });
}

// Authenticate by refresh token on URL
if (location.hash) {
  const hashParams = parseHashParams(location.hash);

  if (hashParams?.tk) {
    // Valida shopWindowId antes de passar para auth/init
    const shopWindowId = hashParams.shopWindow &&
                         hashParams.shopWindow !== 'undefined' &&
                         hashParams.shopWindow !== 'null'
                         ? hashParams.shopWindow
                         : null;

    await store.dispatch("auth/init", {
      refreshToken: hashParams.tk,
      shopWindowId: shopWindowId,
    });
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }
}

// Redirect to my-courses se já autenticado. Usa onReady wrapper pra evitar a race
// entre o router.replace chamado aqui e a "initial navigation" disparada pelo
// Vue.$mount('#app') logo depois. Sem onReady, o $mount dispara uma resolução
// pra location.href atual ('/') que cancela o replace em flight com
// "Navigation cancelled from / to /p/my-courses with a new navigation".
//
// O `.finally` libera o flag `auth.initializing` só DEPOIS do replace acontecer
// — evita flash visual do Login.vue (que tem v-show="!authInitializing"):
// initializing=true durante toda a transição inicial → Login fica hidden →
// sem flash → replace completa → initializing=false (mas Login já foi unmounted).
let _willRedirect = false;
function redirectToMyCoursesWhenReady() {
  _willRedirect = true;
  // Sinal próprio do fim deste redirect: o pushRouter espera por ele antes de
  // abrir a tela do toque num push (ver auth.bootRedirectPending).
  store.commit("auth/SET_BOOT_REDIRECT_PENDING", true);
  router.onReady(() => {
    // A navegação pro my-courses tem guard async lento (checkAccessMiddleware
    // carrega user/enroll/notif). Durante essa janela, uma navegação concorrente
    // do boot cancela a navegação pendente (Vue Router 3) — o push/replace é
    // monkey-patchado com .catch, então a falha vem como valor resolvido (um
    // NavigationFailure, que é um Error → .name é "Error", não a rota). Resultado:
    // ficava preso no login. Fix: re-tenta até commitar de fato no my-courses.
    const tryGo = (attempt) => {
      router.replace("/p/my-courses").then(() => {
        const committed = router.currentRoute && router.currentRoute.name === 'my-courses';
        if (!committed && attempt < 5) {
          setTimeout(() => tryGo(attempt + 1), 60);
        } else {
          store.commit("auth/SET_INITIALIZING", false);
          store.commit("auth/SET_BOOT_REDIRECT_PENDING", false);
        }
      });
    };
    tryGo(1);
  });
}

// Safety net: se algo travar o boot (onReady nunca dispara, replace pendurado,
// middleware async sem next, etc), libera o flag pra não deixar o user com tela
// branca infinita (Login.vue hidden por v-show, AppLayout sem conteúdo).
//
// MAS não enquanto houver prompt biométrico aberto. O `verifyIdentity` fica
// pendurado no TEMPO DE REAÇÃO DO USUÁRIO, não em I/O — Face ID errando uma vez
// já come 3-4s e o prompt do Android espera o dedo indefinidamente. Com o
// timeout fixo, o form de login aparecia POR BAIXO do prompt e a tela pulava
// pro my-courses quando a digital era aceita. O timer é agendado ANTES de o
// prompt abrir, então ele não tinha como saber sozinho.
//
// Cap absoluto pra não trocar "boot travado" por "espera infinita": se o prompt
// nunca resolver (bug no plugin, promise pendurada), libera mesmo assim.
const BOOT_RELEASE_MS = 5000;
const BOOT_RELEASE_MAX_MS = 60000;
const bootStartedAt = Date.now();

function releaseInitializingWhenSafe() {
  if (!store.state.auth.initializing) return;

  const waited = Date.now() - bootStartedAt;
  if (KeychainHandler.isUnlockInFlight() && waited < BOOT_RELEASE_MAX_MS) {
    setTimeout(releaseInitializingWhenSafe, 500);
    return;
  }

  console.warn(
    `[auth] boot initializing timeout (${Math.round(waited / 1000)}s) — liberando flag por safety`
  );
  store.commit("auth/SET_INITIALIZING", false);
}

setTimeout(releaseInitializingWhenSafe, BOOT_RELEASE_MS);

// IMPORTANTE: usar window.location.pathname, NÃO router.currentRoute.path. No
// boot (antes do $mount disparar a navegação inicial) o router ainda está em
// START_LOCATION ('/'), então currentRoute.path é SEMPRE '/' aqui — o que fazia
// qualquer deep-link refrescado (ex.: /p/course/:id/class/:id) cair pra
// my-courses. pathname reflete a URL real que o usuário abriu.
if (["/", "/login"].includes(window.location.pathname)) {
  // Padrão 4: access token ainda válido localmente → entra direto, sem rede.
  // Relógio do cliente adiantado torna isto false com token bom — ok AQUI:
  // cai nos ramos de refresh abaixo (validação por rede). Não transformar em
  // guard silencioso como o antigo do user/getCurrent (Story-5449).
  if (AuthService.isAccessTokenLocallyValid()) {
    redirectToMyCoursesWhenReady();
  } else if (isApp) {
    // Native: reAuthenticate trata refresh, offline-gracioso e fallback keychain
    await store.dispatch("auth/reAuthenticate");
    if (store.getters["auth/isAuthenticated"]) {
      redirectToMyCoursesWhenReady();
    }
  } else if (AuthService.hasRefreshToken()) {
    // Web: tenta refresh; offline não destrói tokens (pattern 1)
    try {
      await AuthService.refreshTokens();
      redirectToMyCoursesWhenReady();
    } catch (error) {}
  }
}

// Se não vai redirecionar (user não autenticado, sem hash, etc), libera o flag
// imediato pra Login.vue renderizar. Caso contrário, o flag será limpo pelo
// `.finally` do replace dentro de redirectToMyCoursesWhenReady.
if (!_willRedirect) {
  store.commit("auth/SET_INITIALIZING", false);
}

// Conserta o estado offline antes de qualquer playback.
//
// Os dois cobrem a mesma falha vista de ângulos diferentes: aula marcada como
// baixada que não toca — e que também não cai pro online, porque o
// localVideoSrc prefere a fonte local enquanto o status for 'downloaded'. Sem
// índice o gate nativo nega a chave (403); com path órfão a mídia não é
// encontrada nem com a chave liberada. Paths primeiro, por isso.
//
// Fora do gate de auth/rede abaixo de propósito: são escrita puramente local,
// não dependem de sessão nem de conexão. Sob aquele gate, quem abrisse o app
// deslogado e entrasse em seguida ficaria sem cura até o próximo lançamento.
if (isApp) {
  store.dispatch('download/healOfflinePaths')
    .then(() => store.dispatch('download/healLoEnrollIndex'))
    .catch(() => {});
}

// Boot resume: tenta retomar downloads pausados (offline → fechou app → reabriu).
// Roda AQUI (não no Network.getStatus().then acima) pra garantir que o auth
// setup já completou — senão dispara fetchManifest sem token válido, http
// interceptor entra em refresh paralelo ao reAuthenticate, segundo refresh
// falha, tokens são apagados, user vai pro login a cada boot.
// Guard adicional dentro de resumePausedDownloads (auth/isAuthenticated) é
// defesa em profundidade pra outros caminhos que dispatch a action.
if (isApp && store.getters["auth/isAuthenticated"] && bootNetworkStatus) {
  // Reusa a Promise da chamada anterior ao Network.getStatus — sem fazer
  // round-trip extra pela bridge Capacitor. `connected` é re-checado aqui
  // porque o state pode ter mudado entre o commit inicial e este ponto
  // (improvável dada a sincronia, mas defensive).
  bootNetworkStatus.then(async ({ connected, connectionType }) => {
    if (connected) {
      // ANTES do resume: o plugin nativo auto-retoma batches que sobraram de um
      // kill de processo, mas o JS não sabe deles (o boot rebaixa tudo pra
      // 'idle'). Sem reconciliar, o nativo baixava o vídeo inteiro e ninguém
      // finalizava — bytes no disco com a UI dizendo "não baixado", e sem
      // marcador de parcial nem dava pra remover. Aqui o órfão vira um parcial
      // normal, que o resume abaixo trata pelo caminho já testado.
      await store.dispatch('download/reconcileOrphanBatches').catch(() => {});
      store.dispatch('download/resumePausedDownloads', { connectionType });
      // Renovação/revogação do lease via lista FRESCA de enrolls (→ lease/
      // syncEntitlements). Disparo EXPLÍCITO aqui, não só via middleware: o
      // checkAccessMiddleware PULA o getUserEnroll quando o currentUser já foi
      // hidratado do cache antes do router (ex.: fallback offline-transitório
      // do reAuthenticate) — sem este disparo o sync não rodaria nesse boot.
      // getUserEnroll é idempotente; eventual duplicação com o middleware é benigna.
      store.dispatch('enroll/getUserEnroll')
        // Ver comentário no listener de reconexão: a cura precisa de token já
        // instalado no axios, o que no boot não vale para este ponto.
        .then(() => store.dispatch('download/healMissingAiCache'))
        .then(() => store.dispatch('download/healMissingSubtitles'))
        .catch(() => {});
      // Sobe o que ficou na fila offline. Sem isto o ÚNICO gatilho em produção é
      // a transição offline→online (networkStatusChange): quem assiste no metrô,
      // tem o app morto pelo SO e reabre já no Wi-Fi nunca observa essa transição
      // — a fila fica parada indefinidamente e, se ele limpar dados antes do
      // próximo flapping de rede, o progresso é perdido. O flush tem guard de
      // reentrância, então concorrer com o do reconnect é inofensivo.
      store.dispatch('enroll/flushOfflineEventQueue').catch(() => {});
    } else {
      // Offline no boot: hidrata cache JS dos arquivos nativos pra que getters
      // de playableState retornem o estado real (em vez de 'unknown' falso por
      // localStorage limpo). Importante quando user limpou dados do app sem
      // desinstalar — arquivos nativos sobrevivem.
      store.dispatch('lease/hydrateFromNative').catch(() => {});
    }
  });
}


new Vue({
  name: 'Root',
  router,
  store,
  beforeMount() {
    const linkEl = document.createElement("link");
    linkEl.rel = "icon";
    linkEl.href = "/favicon-pagtrust.ico";

    window.document.head.appendChild(linkEl);
  },
  mounted() {
    store.commit('dom/SET_WINDOW_WIDTH', window.innerWidth)
    window.addEventListener('resize', () => store.commit('dom/SET_WINDOW_WIDTH', window.innerWidth))
    Vue.prototype.$appName = process.env.VUE_APP_DOMAIN_TITLE;
    Vue.prototype.$appLogoSrc = require(`./assets/images/${process.env.VUE_APP_LOGO_FILE_NAME}`);


  },

  beforeDestroy() {
    window.removeEventListener('resize', () => store.commit('dom/SET_WINDOW_WIDTH', window.innerWidth))
  },
  render: h => h(AppLayout)
}).$mount('#app');
